import axios from 'axios';
const BASE_URL = '/matters';
const mapSplitToAllocation = split => ({
  id: split.matterId,
  client: {
    id: split.clientId,
    name: split.clientLabel
  },
  matter: {
    id: split.matterId,
    name: split.matterLabel
  },
  percentage: split.percentage,
  isPrimary: split.isPrimary
});
class SplitBillingService {
  async loadAllocations(matterId) {
    if (!matterId) {
      throw new Error('Matter ID is required');
    }
    try {
      const response = await axios.get(`${BASE_URL}/${matterId}/split-billing`);
      const splits = response.data.splits || [];
      return splits.map(mapSplitToAllocation);
    } catch (error) {
      console.error('Failed to load split billing allocations:', error);
      throw error;
    }
  }
  async getPrimaryMatter(matterId) {
    if (!matterId) {
      throw new Error('Matter ID is required');
    }
    try {
      const response = await axios.get(`${BASE_URL}/${matterId}/primary-matter`);
      return response.data;
    } catch (error) {
      console.error('Failed to load primary matter info:', error);
      throw error;
    }
  }

  /**
   * Shows restriction dialog for secondary split billing matters
   * @param {string} matterId - The ID of the matter to check
   * @param {string} term - The term to use (e.g., 'Matter', 'matter')
   * @param {string} action - The action being attempted ('Archive' or 'Restore')
   * @returns {Promise<boolean>} - Returns true if action is allowed, false if restricted
   */
  async showActionRestrictionIfNeeded(matterId, term = 'matter', action = 'Archive') {
    try {
      const primary = await this.getPrimaryMatter(matterId);
      const primaryClientName = primary && primary.client ? primary.client.name : 'the primary client';
      const primaryMatterName = primary && primary.name ? primary.name : 'the primary matter';
      const actionVerb = action.toLowerCase();
      const actionGerund = action === 'Archive' ? 'archiving' : 'restoring';
      const errorMessage = `<h4>Cannot ${action} Secondary Split Billing ${term}</h4><p>This ${term} is part of a split billing relationship with ${primaryClientName} and can only be ${actionVerb}d by ${actionGerund} ${primaryMatterName} or removing it from the split billing.</p>`;
      bootbox.alert({
        message: errorMessage,
        buttons: {
          ok: {
            label: 'Close'
          }
        }
      });
      return false; // Cannot perform action
    } catch (err) {
      console.error('Error fetching primary matter:', err);
      const actionVerb = action.toLowerCase();
      const actionGerund = action === 'Archive' ? 'archiving' : 'restoring';
      const errorMessage = `<h4>Cannot ${action} Secondary Split Billing ${term}</h4><p>This ${term} is part of a split billing relationship and can only be ${actionVerb}d by ${actionGerund} the primary matter or removing it from the split billing.</p>`;
      bootbox.alert({
        message: errorMessage,
        buttons: {
          ok: {
            label: 'Close'
          }
        }
      });
      return false; // Cannot perform action
    }
  }

  /**
   * Shows archive restriction dialog for secondary split billing matters
   * @param {string} matterId - The ID of the matter to check
   * @param {string} term - The term to use (e.g., 'Matter', 'matter')
   * @returns {Promise<boolean>} - Returns true if matter can be archived, false if restricted
   */
  async showArchiveRestrictionIfNeeded(matterId, term = 'matter') {
    return this.showActionRestrictionIfNeeded(matterId, term, 'Archive');
  }

  /**
   * Shows restore restriction dialog for secondary split billing matters
   * @param {string} matterId - The ID of the matter to check
   * @param {string} term - The term to use (e.g., 'Matter', 'matter')
   * @returns {Promise<boolean>} - Returns true if matter can be restored, false if restricted
   */
  async showRestoreRestrictionIfNeeded(matterId, term = 'matter') {
    return this.showActionRestrictionIfNeeded(matterId, term, 'Restore');
  }
  async createAllocation(matterId, allocation) {
    if (!matterId) {
      throw new Error('Matter ID is required');
    }
    try {
      const response = await axios.post(`${BASE_URL}/${matterId}/split-billing`, allocation);
      return mapSplitToAllocation(response.data);
    } catch (error) {
      console.error('Failed to create split billing allocation:', error);
      throw error;
    }
  }
  async updateAllocation(allocationId, updates) {
    if (!allocationId) {
      throw new Error('Matter ID and Allocation ID are required');
    }
    try {
      const response = await axios.put(`${BASE_URL}/${allocationId}/split-billing`, updates);
      return mapSplitToAllocation(response.data);
    } catch (error) {
      console.error('Failed to update split billing allocation:', error);
      throw error;
    }
  }
  async deleteAllocation(allocationId) {
    if (!allocationId) {
      throw new Error('Allocation ID is required');
    }
    try {
      await axios.delete(`${BASE_URL}/${allocationId}/split-billing`);
    } catch (error) {
      console.error('Failed to delete split billing allocation:', error);
      throw error;
    }
  }
}
const splitBillingService = new SplitBillingService();
export default splitBillingService;